// https://pinia.vuejs.org/core-concepts/
import { defineStore } from 'pinia';

// It is convention to prefix store names here with "use"
export const useAdvisorStore = defineStore('advisor', {
  state: () => ({
    csrfToken: null,
    advisor: null,
    activeOAuthConnections: {},
    firmLogoBase64: null,
    vuid: null, // Visitor ID
    firmAdvisorCoreSettings: {
      portfolio_color: '#40B6D8',
      benchmark_color: '#8C67EF',
      proposed_color: '#27A683',
    },
    canEditFirmSettings: false,
    advisorHoldingsFormat: null, // custom holdings format
    portfolioFeeDefaults: null, // {starting, proposed, benchmark} per-portfolio fee defaults
    holdings_export_rules: [], // Add default empty rules array
    pageSettings: {}, // New state
    people: {
      isFetching: false,
      hasFetched: false,
      items: [],
      page: 1,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
      defaultSort: 'updated_at',
      defaultDir: 'desc',
      sort: 'updated_at',
      dir: 'desc',
    },
    recommendations: {
      isFetching: false,
      hasFetched: false,
      items: [],
      entities: [],
      advisors: [],
      filters: {
        advisors: [],
        entities: [],
        type: [],
      },
    },
    tasks: {
      isFetching: false,
      hasFetched: false,
      items: [],
      allAdvisors: [],
      advisorsWithTasks: [],
      allEntities: [],
      entitiesWithTasks: [],
      advisors: [],
      filters: {
        search: '',
        status: '',
        assignees: [],
        entities: [],
        priorities: [],
        dueDateBefore: '',
        dueDateAfter: '',
        overdue: false,
      },
    },
  }),

  getters: {
    featureFlags(state) {
      return (
        state.advisor?.user?.feature_flags.map(flag =>
          flag.replace('Feature: ', '').trim(),
        ) || []
      );
    },
    hasFeature() {
      return feature => {
        return this.featureFlags.includes(feature);
      };
    },
    headers() {
      return {
        'X-CSRFToken': this.csrfToken,
        'Content-Type': 'application/json',
      };
    },
  },

  actions: {
    setCSRF(token) {
      this.csrfToken = token;
    },
    setAdvisor(newAdvisor) {
      this.advisor = newAdvisor;
    },
    setFirmLogoBase64(newLogo) {
      this.firmLogoBase64 = newLogo;
    },
    setVisitorID(newID) {
      this.vuid = newID;
    },
    setFirmAdvisorCoreSettings(newSettings) {
      this.firmAdvisorCoreSettings = {
        portfolio_color: '#40B6D8',
        benchmark_color: '#8C67EF',
        proposed_color: '#27A683',
        ...(newSettings || {}),
      };
    },
    setCanEditFirmSettings(newValue) {
      console.log('Setting canEditFirmSettings to', newValue);
      this.canEditFirmSettings = newValue;
      console.log('canEditFirmSettings is now', this.canEditFirmSettings);
    },
    setPageSettings(pageSettings) {
      // New action
      this.pageSettings = pageSettings;
    },
    setAdvisorHoldingsFormat(format) {
      // set the custom holdings format
      this.advisorHoldingsFormat = format;
    },
    setPortfolioFeeDefaults(defaults) {
      this.portfolioFeeDefaults = defaults;
    },
    setHoldingsExportRules(rules) {
      this.holdings_export_rules = rules;
    },
    setFetchingPeople(newValue) {
      this.people.isFetching = newValue;
    },
    setPeople(data) {
      const {
        items,
        page,
        total_pages: totalPages,
        has_next: hasNext,
        has_previous: hasPrevious,
        sort,
        dir,
        search_matched_external_ids: searchMatchedExternalIds,
      } = data;
      this.people.items = items;
      this.people.page = page;
      this.people.totalPages = totalPages;
      this.people.hasNext = hasNext;
      this.people.hasPrevious = hasPrevious;
      this.people.hasFetched = true;
      this.people.sort = sort;
      this.people.dir = dir;
      this.people.searchMatchedExternalIds = searchMatchedExternalIds || [];
    },
    async fetchPeople({
      page,
      search,
      dir,
      sort,
      type,
      hideSharedWithMe = false,
      hasVadis = false,
    } = {}) {
      this.setFetchingPeople(true);

      const searchParams = new URLSearchParams();
      let params = '';

      if (hideSharedWithMe) {
        searchParams.append('hide_shared', true);
      }

      if (hasVadis) {
        searchParams.append('has_vadis', true);
      }

      if (sort && sort !== this.people.defaultSort) {
        searchParams.append('sort', sort);
      }
      if (dir && dir !== this.people.defaultDir) {
        searchParams.append('dir', dir);
      }
      if (page && page > 1) searchParams.append('page', page);
      if (search) searchParams.append('search', search);
      if (type) searchParams.append('type', type);

      if (searchParams.size > 0) {
        params = '?' + searchParams.toString();
      }

      try {
        const response = await fetch(`/api/v1/entities/get_all/${params}`);
        const data = await response.json();
        this.setPeople(data);
      } catch (error) {
        console.error('Error fetching entity data:', error);
      } finally {
        this.setFetchingPeople(false);
      }
    },
    async can(action, objectType, externalId) {
      const res = await fetch(`/api/v1/can/${action}/${objectType}/${externalId}/`, {
        headers: this.headers,
      });

      return res.ok;
    },
  },
});
